import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, X, Send, Loader2, Sparkles, Trash2, GraduationCap, MessageSquare } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
}

type AssistantMode = 'general' | 'kfs';

const GENERAL_PROMPTS = [
  { label: 'Statusy wniosków', prompt: 'Wyjaśnij mi kolejność statusów wniosku PUP i co oznacza każdy z nich' },
  { label: 'Napisz maila', prompt: 'Napisz profesjonalny mail do klienta z informacją o pozytywnej decyzji dotacyjnej' },
  { label: 'Statystyki', prompt: 'Jakie są aktualne statystyki systemu? Ile mamy aktywnych wniosków?' },
  { label: 'Produkty FPN', prompt: 'Wymień wszystkie produkty i usługi Fundacji Promocji Nowej Huty' },
  { label: 'KFS procedura', prompt: 'Jak wygląda procedura ubiegania się o szkolenie KFS krok po kroku?' },
  { label: 'Dotacje rozwojowe', prompt: 'Jakie dotacje na rozwój firmy oferujemy i jakie są wymagania?' },
];

const KFS_PROMPTS = [
  { label: 'Który priorytet?', prompt: 'Który priorytet KFS 2026 najlepiej pasuje do szkolenia z AI dla salonu beauty? Uzasadnij wybór.' },
  { label: 'Ile de minimis?', prompt: 'Jak obliczyć limit pomocy de minimis? Ile wynosi aktualny limit i jak go sprawdźić?' },
  { label: 'Checklist dokumentów', prompt: 'Wymień kompletną listę dokumentów potrzebnych do wniosku KFS z opisem każdego' },
  { label: 'Dopasuj szkolenie', prompt: 'Jakie szkolenie z naszej oferty najlepiej pasuje pod priorytet 3 KFS (nowe technologie i AI)?' },
  { label: 'Wylicz dofinansowanie', prompt: 'Jak wyliczyć dofinansowanie KFS? Ile dostanie mikrofirma (<10 os.) a ile większa firma?' },
  { label: 'Procedura krok po kroku', prompt: 'Opisz procedurę składania wniosku KFS krok po kroku — od wyboru PUP do rozliczenia' },
];

export const AIAssistantPanel: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<AssistantMode>('general');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();

  // Auto-switch to KFS mode on KFS pages
  useEffect(() => {
    const isKfsPage = location.pathname.includes('kfs') || location.pathname.includes('szkolenia');
    if (isKfsPage && messages.length === 0) setMode('kfs');
  }, [location.pathname]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: Message = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, userMsg].slice(-20);
      if (mode === 'kfs') {
        const response = await crmApi.kfsAdvisor(text.trim(), history);
        setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      } else {
        const { response, sources } = await crmApi.aiAssistant(text.trim(), history, location.pathname);
        setMessages(prev => [...prev, { role: 'assistant', content: response, sources }]);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Przepraszam, wystąpił błąd. Spróbuj ponownie za chwilę.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-[420px] max-w-[calc(100vw-3rem)] bg-white shadow-2xl border-l border-slate-200 z-50 flex flex-col">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 text-white shrink-0 ${
        mode === 'kfs'
          ? 'bg-gradient-to-r from-emerald-700 to-teal-800'
          : 'bg-gradient-to-r from-slate-800 to-slate-900'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            mode === 'kfs' ? 'bg-emerald-500/20' : 'bg-blue-500/20'
          }`}>
            {mode === 'kfs' ? <GraduationCap size={18} className="text-emerald-300" /> : <Bot size={18} className="text-blue-300" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold">{mode === 'kfs' ? 'Doradca KFS' : 'AI Asystent'}</h3>
            <p className="text-[10px] text-slate-400">{mode === 'kfs' ? 'KFS 2026 • Priorytety • De minimis' : 'GrantOS • Claude + RAG'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setMode(m => m === 'kfs' ? 'general' : 'kfs'); setMessages([]); }}
            className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
              mode === 'kfs'
                ? 'bg-emerald-600/50 text-emerald-200 hover:bg-emerald-600/70'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/70'
            }`}
            title={mode === 'kfs' ? 'Przełącz na ogólny' : 'Przełącz na KFS'}
          >
            {mode === 'kfs' ? <><MessageSquare size={10} className="inline mr-1" />Ogólny</> : <><GraduationCap size={10} className="inline mr-1" />KFS</>}
          </button>
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              title="Wyczyść rozmowę"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center pt-8">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
              mode === 'kfs'
                ? 'bg-gradient-to-br from-emerald-500 to-teal-600'
                : 'bg-gradient-to-br from-blue-500 to-indigo-600'
            }`}>
              {mode === 'kfs' ? <GraduationCap size={28} className="text-white" /> : <Sparkles size={28} className="text-white" />}
            </div>
            <h4 className="text-lg font-semibold text-slate-800 mb-1">
              {mode === 'kfs' ? 'Doradca KFS 2026' : 'Witaj! Jak mogę pomóc?'}
            </h4>
            <p className="text-sm text-slate-500 mb-6">
              {mode === 'kfs'
                ? 'Ekspert ds. Krajowego Funduszu Szkoleniowego. Priorytety, de minimis, wnioski.'
                : 'Jestem Twoim asystentem AI. Mogę pomóc z procedurami, tworzeniem treści i analizą danych.'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(mode === 'kfs' ? KFS_PROMPTS : GENERAL_PROMPTS).map(qp => (
                <button
                  key={qp.label}
                  onClick={() => sendMessage(qp.prompt)}
                  className={`text-left px-3 py-2 rounded-xl border text-xs transition-colors ${
                    mode === 'kfs'
                      ? 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 hover:border-emerald-400 text-emerald-700'
                      : 'bg-slate-50 hover:bg-blue-50 border-slate-200 hover:border-blue-300 text-slate-700 hover:text-blue-700'
                  }`}
                >
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-md'
                  : 'bg-slate-100 text-slate-800 rounded-bl-md'
              }`}
            >
              <div className="whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{
                  __html: msg.role === 'assistant'
                    ? msg.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/^- /gm, '• ')
                        .replace(/^(\d+)\. /gm, '$1. ')
                        .replace(/`(.*?)`/g, '<code class="bg-slate-200 px-1 rounded text-xs">$1</code>')
                    : msg.content.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                }}
              />
              {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Źródła:</span>
                  {msg.sources.map((src, i) => (
                    <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">{src}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" />
                Myślę...
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-slate-200 p-3 bg-slate-50">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Napisz wiadomość..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            style={{ maxHeight: '120px' }}
            onInput={e => {
              const el = e.target as HTMLTextAreaElement;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 text-center">
          Shift+Enter = nowa linia • Enter = wyślij
        </p>
      </div>
    </div>
  );
};
